import React, { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useMsal } from "@azure/msal-react"
import { Link, useNavigate } from "react-router-dom"
import moment from "moment"

import { changeDateValue, changeLocationValue, changeStatusValue, changeUserValue } from "../../store/slices/CateringOrderInputStatesSlice"
import { getAllLanguages, getCanteenUserProfile, getCurrentUser, getTranslationWithPageName, switchLanguage } from "../../services/ApiServices"
import { convertRespArrToLabelObject, getDynamicLabel, handleError, isTypeOf } from "../../utils/helpers"
import { appName, catering_admin_version, localStorageLocationKey } from "../../const"
import { changeOrderTranslationValue } from "../../store/slices/TranslationSlice.ts"
import { changeAllLanguagesValue, changeCurrentUserValue } from "../../store/slices/APIResponseSlice.js"
import { changeAuthenticated } from "../../store/slices/MSALSlice"
import { PAGE_NAME } from "../../utils/pageName.ts"
import { loginRequest } from "../../authConfig"
import Loader from "../Loader/Loader"
import "./Header.css"

// ==== Imported Image ====
import cipLogo from "../../assets/img/Cip-logo.png"
import userThumb from "../../assets/img/user_thumb.jpg"
import iconChevronDown from "../../assets/img/icon-chevron-down.svg"
import Select from "react-select"
import toast from "react-hot-toast"
import CreatableSelect from "react-select/creatable"


export default function Header() {
  const orderTranslation = useSelector((state) => state.Translation.OrderTranslation)
  const selectedLocation = useSelector((state) => state.InputStates.inputLocation)
  const currentUser = useSelector((state) => state.APIResponse.CurrentUser)
  const allLanguages = useSelector((State) => State.APIResponse.AllLanguages)

  const [profileDropdown, setProfileDropdown] = useState(false)
  const [profileDropdownHoverStatus, setProfileDropdownHoverStatus] = useState(false)
  const [userImageUrl, setUserImageUrl] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [selectedLanguage, setSelectedLanguage] = useState(currentUser?.LanguageId)
  const [modalOpen, setModalOpen] = useState(false)



  const { instance } = useMsal()

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const body = document.body.classList

  const userName = useSelector((state) => state.MSALAuth.userName)
  const userEmail = useSelector((state) => state.MSALAuth.userEmail)

  const toggleSideBar = () => {
    body.contains("toggle-sidebar")
      ? body.remove("toggle-sidebar")
      : body.add("toggle-sidebar")
  }

  const handleLogout = () => {
    instance.logoutRedirect(loginRequest)
    dispatch(changeAuthenticated(false))
  }

  const setDefaultOrderInputValue = () => {
    dispatch(changeUserValue(""))
    dispatch(changeLocationValue(selectedLocation))
    dispatch(changeDateValue(moment().format("YYYY-MM-DD")))
    dispatch(changeStatusValue([]))
  }

  // Get current user and Translation
  const handleLanguageChanges = async () => {
    Promise.all([getCurrentUser(), getTranslationWithPageName(PAGE_NAME?.ORDERS, appName)]).then((response) => {
      const currentUserResponse = response[0]
      const OrderTranslationResponse = response[1]

      if (isTypeOf(currentUserResponse, "object") && currentUserResponse?.Email) {
        dispatch(changeCurrentUserValue(currentUserResponse))
      } else {
        handleError(currentUserResponse, "Get Current User Error:")
      }

      if (typeof OrderTranslationResponse === "object" && OrderTranslationResponse?.length >= 0) {
        dispatch(changeOrderTranslationValue(convertRespArrToLabelObject(OrderTranslationResponse)))
      } else {
        handleError(OrderTranslationResponse)
      }
    }).catch((error) => {
      handleError(error)
    }).finally(() => {
      setIsLoading(false)
    })
    return
  }

  const SwitchLanguage = (languageId) => {
    if (languageId) {
      setIsLoading(true)
      switchLanguage(languageId).then(async (response) => {
        await handleLanguageChanges()
        setProfileDropdown(false)
        if (!response) {
          response === "" && toast.success(getDynamicLabel(orderTranslation?.msgSuccessSwitchLanguage, "Language Switched Successfully."))
        }
      }).catch((error) => {
        handleError(error)
      }).finally(() => {
        setIsLoading(false)
        setModalOpen(false)
        setProfileDropdown(false)
        navigate("/")
      })
    }
  }

  useEffect(() => {
    localStorage?.setItem(localStorageLocationKey, JSON.stringify(selectedLocation))
  }, [selectedLocation])

  useEffect(() => {
    userEmail && getCanteenUserProfile(userEmail).then((response) => setUserImageUrl(response))
  }, [userEmail])

  useEffect(() => {
    setIsLoading(true)
    getAllLanguages().then((languageResponse) => {
      if (typeof languageResponse === "object" && languageResponse?.length >= 0) {
        dispatch(changeAllLanguagesValue(languageResponse && languageResponse?.length > 0 ? languageResponse : []))
        setSelectedLanguage(currentUser?.LanguageId)
      } else {
        handleError(languageResponse)
      }
    }).catch((error) => {
      handleError(error)
    }).finally(() => {
      setIsLoading(false)
    })
  }, [dispatch, currentUser?.LanguageId])


  return (
    <>
      {isLoading && <Loader />}
      <header
        id="header"
        className="header fixed-top d-flex align-items-center"
      >
        <div className="header-inner" >
          <i onClick={() => toggleSideBar()} className="bi bi-list toggle-sidebar-btn" />
          <div className="d-flex align-items-center justify-content-between">
            <Link to="/" className="logo d-flex align-items-center">
              <img className="header-logo" src={cipLogo} alt="MyDesk" />
            </Link>
          </div>
        </div>

        <nav className="header-nav ms-auto">
          <ul className="d-flex align-items-center">
            <li className="nav-item dropdown">
              <Link
                className="nav-link gap-2 nav-profile d-flex align-items-center pe-0 show"
                data-bs-toggle="dropdown" aria-expanded="true"
              >
                <img
                  src={userImageUrl?.Image ? userImageUrl?.Image : userThumb}
                  className="rounded-circle currentUserProfile" alt="Profile"
                />
                <span className="d-none d-md-block dropdown-toggle m-0">
                  {getDynamicLabel(orderTranslation?.lblHi, "Hi")}, <span>{userName}</span>
                </span>
                <button
                  className="dropdown" tabIndex={0}
                  onBlur={() => !profileDropdownHoverStatus && setProfileDropdown(false)}
                  onClick={() => setProfileDropdown(!profileDropdown)}
                >
                  <i className="icon">
                    <img src={iconChevronDown} alt="icon-chevron-down" />
                  </i>
                </button>
              </Link>

              {profileDropdown && (
                <ul
                  className="dropdown-menu dropdown-menu-end dropdown-menu-arrow profile position-absolute m-0 show"
                  data-popper-placement="bottom-end"
                  onMouseEnter={() => setProfileDropdownHoverStatus(true)}
                  onMouseLeave={() => setProfileDropdownHoverStatus(false)}
                  style={{
                    inset: "0px 0px auto auto",
                    transform: "translate3d(0px, 54px, 0px)",
                  }}
                >
                  <li className="dropdown-header">
                    <h6 id="hDisplayName">{userName}</h6>
                    <span id="spADUserMAil">{userEmail}</span>
                  </li>
                  <li><hr className="dropdown-divider" /></li>
                  <li
                    className="my-2"
                    style={{ cursor: "pointer" }}
                    // data-bs-toggle="modal"
                    // data-bs-target="#switchLanguageModal"
                    onClick={() => {
                      setModalOpen(true)
                      setProfileDropdown(false)
                    }}
                  >
                    <span className="d-flex align-items-center justify-content-start gap-2 ms-4">
                      <i className="bi bi-translate app-theme-color" title="Language" style={{ fontSize: "23px" }} />
                      {getDynamicLabel(orderTranslation?.lblSwitchLanguage, "Switch Language")}
                    </span>
                  </li>
                  <li><hr className="dropdown-divider" /></li>
                  <li style={{ cursor: "pointer" }} className="d-flex align-items-center justify-content-center" >
                    <span className="d-flex align-items-center justify-content-start dropdown-item " >
                      <Link
                        to="#"
                        onClick={() => handleLogout()}
                        className=" d-flex align-items-center ms-2"
                        style={{ textDecoration: "none", listStyle: "none", color: "black" }}
                      >
                        <i className="bi bi-box-arrow-right" style={{ fontSize: "23px" }} />
                        <span  >{getDynamicLabel(orderTranslation?.btnSignOut, "Sign Out")}</span>
                      </Link>
                    </span>
                  </li>
                </ul>
              )}
            </li>
          </ul>
        </nav>
      </header >

      {/* =============================== Switch Language Modal =============================== */}
      {modalOpen && <div className="modal-backdrop" style={{
        backdropFilter: "blur(5px)", backgroundColor: "#e3d9d94a",

      }}
      > <div className={`modal fade mt-5 ${modalOpen ? 'show' : ''}`} style={{ display: modalOpen ? 'block' : 'none' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h1 className="modal-title fs-5" id="exampleModalLabel">{getDynamicLabel(orderTranslation?.lblSwitchLanguage, "Switch Language")}</h1>
                <i className="bi bi-close" data-bs-dismiss="modal" />
              </div>

              <div className="modal-body">
                <div className="row m-4">
                  <div className="col-12">
                    <label className="mb-2">{getDynamicLabel(orderTranslation?.lblLanguages, "Languages")}</label>
                    <select
                      value={selectedLanguage}
                      onChange={(e) => setSelectedLanguage(Number(e.target.value))}
                      className="form-select"
                    >
                      <option value={0}>{getDynamicLabel(orderTranslation?.lblSelectLanguage, "Select Language")}</option>
                      {allLanguages && allLanguages?.length > 0 && (
                        allLanguages?.map((d) =>
                          <option key={d?.Id} value={d?.Id}>
                            {d?.Displayname}
                          </option>
                        )
                      )}
                    </select>
                  </div>
                </div>
              </div>

              <div className="modal-footer text-end">
                <button
                  onClick={() => {
                    setModalOpen(false)
                    setProfileDropdown(false)
                  }}
                  className="btn btn-outline-secondary "
                  data-bs-dismiss="modal"
                >
                  {getDynamicLabel(orderTranslation?.btnCancel, "Cancel")}
                </button>
                <button
                  onClick={() => SwitchLanguage(selectedLanguage)}
                  className="btn btn-primary"
                  data-bs-dismiss="modal" >

                  {getDynamicLabel(orderTranslation?.btnSave, "Save")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div >}

      <aside id="sidebar" className="sidebar">
        <div style={{ position: "relative", height: "calc(100vh - 110px)" }}>
          <ul className="sidebar-nav" id="sidebar-nav">
            <li className="nav-item">
              <Link to="/" onClick={() => { setDefaultOrderInputValue(); toggleSideBar() }} className="nav-link">
                <i className="bi bi-grid" />
                <span>{getDynamicLabel(orderTranslation?.lblDashBoard, "Dashboard")}</span>
              </Link>
            </li>
            <li className="nav-item">
              <Link to="/order/create-order" onClick={() => toggleSideBar()} className="nav-link">
                <i className="bi bi-cart" />
                <span>{getDynamicLabel(orderTranslation?.lblOrder, "Order")}</span>
              </Link>
            </li>
            <li className="nav-item">
              <Link to="/Overview" onClick={() => toggleSideBar()} className="nav-link">
                <i className="bi bi-info-circle" />
                <span>{getDynamicLabel(orderTranslation?.lblOverView, "Overview")}</span>
              </Link>
            </li>
            <li className="nav-item">
              <Link to="/users" onClick={() => toggleSideBar()} className="nav-link">
                <i className="bi bi-person" />
                <span>{getDynamicLabel(orderTranslation?.lblUsers, "Users")}</span>
              </Link>
            </li>
          </ul>

          <span style={{ position: "absolute", bottom: "-15px", left: "5px" }}>
            {catering_admin_version}
          </span>
        </div>
      </aside>
    </>
  )
}
